/**
 * How the admin Alerts page names an alert: the rule in plain English, the
 * charger and site it is about, and when it happened in the site's own
 * time. Pure, unit-tested in alertPresentation.test.ts.
 */
import type { AlertRow } from './api';

/**
 * Every rule id that writes Alert rows: the seven predictive-maintenance
 * rules (apps/api/src/rules/predictive-maintenance/rules.ts), the
 * orphaned charging profile sweep (apps/api/src/lib/orphanProfileSweep.ts)
 * and the refused-start sweep (apps/api/src/lib/refusedStartSweep.ts).
 */
const RULE_NAMES: Readonly<Record<string, string>> = {
  'pm.charger_offline': 'Charger offline',
  'pm.recurring_fault': 'Recurring fault',
  'pm.stuck_connector': 'Stuck connector',
  'pm.failed_transactions': 'Failed transactions',
  'pm.power_anomaly': 'Power drop mid-charge',
  'pm.sim_on_air_ocpp_silent': 'SIM online, charger silent',
  'pm.sim_offline': 'SIM offline',
  orphan_charging_profile: 'Charging current capped',
  refused_starts: 'Refusing starts',
};

/** "pm.some_new_rule" to "Some new rule": never the raw id. */
export function humaniseRuleId(ruleId: string): string {
  const tail = ruleId.split('.').pop() ?? '';
  const words = tail
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-\s]+/g, ' ')
    .trim()
    .toLowerCase();
  if (!words) return 'Alert';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function alertRuleName(ruleId: string): string {
  return RULE_NAMES[ruleId] ?? humaniseRuleId(ruleId);
}

export const ALERT_STATUS_LABELS: Readonly<Record<AlertRow['status'], string>> = {
  detected: 'Detected',
  notified: 'Notified',
  action_pending: 'Action pending',
  escalated: 'Escalated',
  resolved: 'Resolved',
};

export const ALERT_SEVERITY_LABELS: Readonly<Record<AlertRow['severity'], string>> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
};

/** The charger's name, falling back to the tail of its id when it can no longer be loaded. */
export function alertChargerName(row: Pick<AlertRow, 'charger' | 'charger_id'>): string {
  return row.charger?.name || `Charger ${String(row.charger_id).slice(-8)}`;
}

/** "Visitor Spot · 500 Wilson", or just the charger when its site is unknown. */
export function alertChargerAndSite(row: Pick<AlertRow, 'charger' | 'charger_id' | 'site'>): string {
  const site = row.site?.name;
  return site ? `${alertChargerName(row)} · ${site}` : alertChargerName(row);
}

export interface AlertTime {
  /** "Oct 23, 2026, 9:14 AM EDT". */
  text: string;
  /** False when there is no usable site zone and the time is shown in UTC. */
  inSiteZone: boolean;
}

function usableZone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

function readTime(value: string | Date | null | undefined): Date | null {
  const at = value ? new Date(value) : null;
  return at && !Number.isNaN(at.getTime()) ? at : null;
}

/**
 * True when the alert's site was loaded and has no usable zone: its times
 * are in UTC and the page says the site's zone is not set. A null site
 * (charger deleted, or outside the caller's tenant) is unknown, not
 * zoneless, so the page makes no claim about it and the time simply reads
 * in UTC with its UTC label.
 */
export function siteZoneMissing(site: AlertRow['site'] | undefined): boolean {
  return site != null && usableZone(site.timezone) === null;
}

/**
 * An alert timestamp in the site's time zone, with the zone's abbreviation
 * for that instant (EDT in summer, EST in winter). A site without a zone is
 * shown in UTC and says so: Location.timezone null means "not known", and
 * guessing a zone would put the wrong hour in front of the operator.
 */
export function formatAlertTime(
  value: string | Date | null | undefined,
  zone: string | null | undefined,
  locale = 'en-US',
): AlertTime {
  const siteZone = usableZone(zone);
  const at = readTime(value);
  if (!at) return { text: 'Not yet', inSiteZone: siteZone !== null };
  const text = new Intl.DateTimeFormat(locale, {
    timeZone: siteZone ?? 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(at);
  return { text, inSiteZone: siteZone !== null };
}

/**
 * A lifecycle timestamp in the alert drawer (notified, acknowledged,
 * resolved, escalated). An empty one reads "Not yet" while the alert is
 * open, since it can still happen, and "Never" once the alert is resolved,
 * since it no longer can.
 */
export function formatAlertLifecycleTime(
  value: string | Date | null | undefined,
  zone: string | null | undefined,
  status: AlertRow['status'] | undefined,
): string {
  if (!readTime(value) && status === 'resolved') return 'Never';
  return formatAlertTime(value, zone).text;
}
